/**
 * Where the nutrition-coverage figure comes from.
 *
 * "12.6% — 9,619 of 76,441" is one number that changes once a night, and it was
 * costing a Pages Function invocation and a database read every time a page
 * asked for it. It is now a file baked at deploy time: served from the edge,
 * no Function, no database, no quota to run out of.
 *
 * The API stays as a fallback, for the window after a nightly rebuild but
 * before the next deploy, and for any build where the file could not be
 * written. A stale percentage would be a worse answer than a fresh one, but a
 * missing percentage is worse than either.
 */

export interface CoveragePayload {
  total: number;
  human: { total: number; withNutrition: number };
  pet: { total: number; withNutrition: number };
  withNutrition: number;
  percent: number;
  sources: {
    retailerLabel: number;
    barcodeExact: number;
    nameMatched: number;
    genericReference: number;
  };
  sweep: { attempted: number; eligible: number; percent: number };
  basisPending: number;
}

interface Envelope {
  ok?: boolean;
  data?: CoveragePayload;
}

/** The baked file first, then the endpoint. Returns null if neither answers. */
export async function fetchCoverage(signal?: AbortSignal): Promise<CoveragePayload | null> {
  const fromFile = await readJson('/data/coverage.json', signal);
  if (fromFile !== null) return fromFile;
  return readJson('/api/coverage', signal);
}

async function readJson(url: string, signal?: AbortSignal): Promise<CoveragePayload | null> {
  try {
    const response = await fetch(url, signal ? { signal } : {});
    if (!response.ok) return null;
    // Pages serves the app shell for an unknown path, so a missing file arrives
    // as 200 text/html rather than 404.
    if (!(response.headers.get('content-type') ?? '').includes('json')) return null;
    const body = (await response.json()) as Envelope;
    if (body.ok !== true || body.data === undefined) return null;
    if (typeof body.data.percent !== 'number') return null;
    return body.data;
  } catch {
    return null;
  }
}
